/**
 * Reusable file upload component with drag-and-drop support.
 *
 * Features:
 * - Drag-and-drop zone with visual feedback
 * - Click-to-browse fallback
 * - File type validation (images, PDFs)
 * - File size validation (configurable, default 10 MB)
 * - Preview of selected file name and size
 * - Remove file button
 *
 * @module components/FileUpload
 */

import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const DEFAULT_MAX_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB
const DEFAULT_ACCEPT = 'image/jpeg,image/png,image/webp,application/pdf';

export interface FileUploadProps {
  /** Unique identifier used for the hidden input's `id` and `name`. */
  id: string;
  /** Human-readable label displayed above the drop zone. */
  label: string;
  /** Accepted MIME types (comma-separated). Defaults to images + PDFs. */
  accept?: string;
  /** Maximum file size in bytes. Defaults to 10 MB. */
  maxSizeBytes?: number;
  /** Called when a valid file is selected (or `null` when removed). */
  onFileChange: (file: File | null) => void;
  /** The currently selected file, if any (controlled). */
  file: File | null;
  /** Optional validation error to display beneath the drop zone. */
  error?: string | null;
  /** Whether the input is disabled (e.g. during upload). */
  disabled?: boolean;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function FileUpload({
  id,
  label,
  accept = DEFAULT_ACCEPT,
  maxSizeBytes = DEFAULT_MAX_SIZE_BYTES,
  onFileChange,
  file,
  error,
  disabled = false,
}: FileUploadProps) {
  const { t } = useTranslation('portal');
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const accessibleLabel = label.trim() || t('fileUpload.defaultLabel');

  const acceptSet = new Set(accept.split(',').map((s) => s.trim()));

  const validate = useCallback(
    (f: File): string | null => {
      if (!acceptSet.has(f.type)) {
        return t('fileUpload.invalidType', {
          type: f.type || t('fileUpload.unknownType'),
        });
      }
      if (f.size > maxSizeBytes) {
        return t('fileUpload.tooLarge', {
          size: formatBytes(f.size),
          maxSize: formatBytes(maxSizeBytes),
        });
      }
      return null;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [maxSizeBytes, accept, t],
  );

  const handleFile = useCallback(
    (f: File) => {
      const err = validate(f);
      if (err) {
        setValidationError(err);
        onFileChange(null);
      } else {
        setValidationError(null);
        onFileChange(f);
      }
    },
    [validate, onFileChange],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) setDragActive(true);
    },
    [disabled],
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      if (disabled) return;
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile) handleFile(droppedFile);
    },
    [disabled, handleFile],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selected = e.target.files?.[0];
      if (selected) handleFile(selected);
    },
    [handleFile],
  );

  const handleRemove = useCallback(() => {
    setValidationError(null);
    onFileChange(null);
    if (inputRef.current) inputRef.current.value = '';
  }, [onFileChange]);

  const displayError = error ?? validationError;

  return (
    <div className="w-full">
      <label htmlFor={id} className="mb-1.5 block text-sm font-semibold text-ink">
        {accessibleLabel}
      </label>

      {file ? (
        /* File selected preview */
        <div className="flex items-center gap-3 rounded-md border border-primary/30 bg-primary-tint px-4 py-3">
          <svg
            className="h-5 w-5 shrink-0 text-[var(--nv-primary-color)]"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-ink">{file.name}</p>
            <p className="text-xs text-ink-muted">{formatBytes(file.size)}</p>
          </div>
          <button
            type="button"
            onClick={handleRemove}
            disabled={disabled}
            className="shrink-0 rounded p-1 text-ink-muted hover:bg-surface-sunken hover:text-ink-muted focus:outline-none focus:ring-2 focus:ring-[var(--nv-primary-color)] disabled:opacity-50"
            aria-label={t('fileUpload.removeFileLabel', { name: file.name })}
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      ) : (
        /* Drop zone */
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => !disabled && inputRef.current?.click()}
          onKeyDown={(e) => {
            if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              inputRef.current?.click();
            }
          }}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-disabled={disabled}
          aria-label={accessibleLabel}
          className={[
            'flex cursor-pointer flex-col items-center justify-center rounded-md border border-dashed px-6 py-10 text-center transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
            dragActive
              ? 'border-[var(--nv-primary-color)] bg-primary-tint'
              : 'border-hairline-strong bg-white hover:border-ink/40',
            disabled ? 'cursor-not-allowed opacity-50' : '',
          ].join(' ')}
        >
          <svg
            className="mb-2 h-8 w-8 text-ink-muted"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 16v-8m0 0l-3 3m3-3l3 3M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5"
            />
          </svg>
          <p className="text-sm text-ink-muted">
            <span className="font-semibold text-[var(--nv-primary-color)]">{t('fileUpload.clickToUpload')}</span>
            {t('fileUpload.orDragDrop')}
          </p>
          <p className="mt-1 text-xs text-ink-muted">
            {t('fileUpload.fileHint', { maxSize: formatBytes(maxSizeBytes) })}
          </p>
        </div>
      )}

      <input
        ref={inputRef}
        id={id}
        name={id}
        type="file"
        accept={accept}
        onChange={handleInputChange}
        disabled={disabled}
        className="sr-only"
        aria-label={accessibleLabel}
        aria-describedby={displayError ? `${id}-error` : undefined}
      />

      {displayError && (
        <p id={`${id}-error`} role="alert" className="mt-1 text-sm text-danger">
          {displayError}
        </p>
      )}
    </div>
  );
}
